/**
 * Risk math for a Morpho Blue collateral/loan position.
 *
 * Conventions (Morpho Blue):
 *   ORACLE_PRICE_SCALE = 1e36
 *   collateralValueRaw (in loan-token wei) = collateralRaw * oraclePrice / 1e36
 *   maxBorrowRaw                            = collateralValueRaw * lltv / 1e18
 *   healthFactor                            = maxBorrowRaw / borrowRaw
 *   liquidationPrice                        = borrowRaw * 1e36 * 1e18 / (collateralRaw * lltv)
 */

export const ORACLE_PRICE_SCALE = 10n ** 36n;
export const WAD = 10n ** 18n;

export type RiskInputs = {
  /** CADC collateral, raw (1e18). */
  readonly collateralRaw: bigint;
  /** USDC borrow assets, raw (1e6). */
  readonly borrowRaw: bigint;
  readonly oraclePrice: bigint;
  readonly lltv: bigint;
  readonly loanDecimals: number;
  readonly collateralDecimals: number;
};

export type RiskMetrics = {
  /** ∞ if no debt. */
  readonly healthFactor: number;
  /**
   * Oracle price (CADC quoted in USDC, decimal-adjusted) at which the
   * position becomes liquidatable. `null` if no borrow / no collateral.
   */
  readonly liquidationPrice: number | null;
  /** Loan-asset value of collateral in human units. */
  readonly collateralValueLoan: number;
  /** Borrow assets in human units. */
  readonly borrowValueLoan: number;
};

/**
 * Translate Morpho's oracle raw price into a CADC→USDC human-readable rate.
 * For a market with collateral 18-dec and loan 6-dec, the conversion is
 * `oraclePrice / 10^(36 + loanDec - collateralDec)`.
 */
const oraclePriceHuman = (
  oraclePrice: bigint,
  loanDecimals: number,
  collateralDecimals: number,
): number => {
  const expo = 36 + loanDecimals - collateralDecimals;
  return Number(oraclePrice) / 10 ** expo;
};

export const computeRisk = (input: RiskInputs): RiskMetrics => {
  const { collateralRaw, borrowRaw, oraclePrice, lltv } = input;

  const collateralValueRaw =
    (collateralRaw * oraclePrice) / ORACLE_PRICE_SCALE;
  const maxBorrowRaw = (collateralValueRaw * lltv) / WAD;

  const borrowValueLoan = Number(borrowRaw) / 10 ** input.loanDecimals;
  const collateralValueLoan =
    Number(collateralValueRaw) / 10 ** input.loanDecimals;

  const healthFactor =
    borrowRaw === 0n
      ? Number.POSITIVE_INFINITY
      : Number(maxBorrowRaw) / Number(borrowRaw);

  let liquidationPrice: number | null = null;
  if (collateralRaw > 0n && borrowRaw > 0n && lltv > 0n) {
    // P* such that collateralRaw * P* * lltv / (1e36 * 1e18) = borrowRaw
    //   ⇒ P*_raw = borrowRaw * 1e54 / (collateralRaw * lltv)
    const p =
      (borrowRaw * ORACLE_PRICE_SCALE * WAD) / (collateralRaw * lltv);
    liquidationPrice = oraclePriceHuman(
      p,
      input.loanDecimals,
      input.collateralDecimals,
    );
  }

  return {
    healthFactor,
    liquidationPrice,
    collateralValueLoan,
    borrowValueLoan,
  };
};

export const formatHealthFactor = (hf: number): string => {
  if (!Number.isFinite(hf)) return "∞";
  if (hf > 999) return "999+";
  return hf.toFixed(2);
};

/**
 * Ajna risk math. Ajna stores quote (USDC) and collateral (CADC) in 1e18 WAD
 * internally regardless of native decimals, and a borrower is liquidatable
 * when their threshold price (TP = debt / collateral, WAD) exceeds the LUP.
 *
 *   healthFactor = LUP / TP     (>= 1 healthy; < 1 ⇒ liquidatable)
 *   liquidationPrice = TP, the LUP at which the position crosses.
 */
export type AjnaRiskInputs = {
  /** CADC collateral, raw 1e18 (Ajna stores in WAD = native for CADC). */
  readonly collateralRaw: bigint;
  /** USDC debt in native (1e6). Will be up-scaled to WAD internally. */
  readonly debtUsdcRaw: bigint;
  /** Pool's current LUP, 1e18 WAD quote-per-collateral. */
  readonly lup: bigint;
};

export const computeAjnaRisk = (input: AjnaRiskInputs): RiskMetrics => {
  const { collateralRaw, debtUsdcRaw, lup } = input;
  const debtWad = debtUsdcRaw * 10n ** 12n; // USDC native (6) → WAD (18).

  const collateralValueWad = (collateralRaw * lup) / WAD;
  const collateralValueLoan = Number(collateralValueWad / 10n ** 12n) / 1e6;
  const borrowValueLoan = Number(debtUsdcRaw) / 1e6;

  const healthFactor =
    debtWad === 0n || collateralRaw === 0n
      ? Number.POSITIVE_INFINITY
      : Number((collateralValueWad * WAD) / debtWad) / 1e18;

  let liquidationPrice: number | null = null;
  if (collateralRaw > 0n && debtWad > 0n) {
    // TP = debt / collateral, both WAD ⇒ result is WAD quote-per-collateral.
    // For display: USDC per CADC = TP / 1e18 (WAD scaling).
    const tpWad = (debtWad * WAD) / collateralRaw;
    liquidationPrice = Number(tpWad) / 1e18;
  }

  return {
    healthFactor,
    liquidationPrice,
    collateralValueLoan,
    borrowValueLoan,
  };
};
